import React from 'react';

const AlertContext = React.createContext(null);

let nextAlertId = 1;

export function AlertProvider({ children }) {
  const [alerts, setAlerts] = React.useState([]);

  const dismissAlert = React.useCallback((id) => {
    setAlerts((current) => current.filter((alert) => alert.id !== id));
  }, []);

  const pushAlert = React.useCallback((input) => {
    const alert = typeof input === 'string'
      ? { message: input }
      : (input || {});

    const id = nextAlertId++;
    const normalized = {
      id,
      type: alert.type || 'info',
      title: alert.title || '',
      message: alert.message || '',
      duration: typeof alert.duration === 'number' ? alert.duration : 5000,
    };

    setAlerts((current) => [normalized, ...current].slice(0, 5));

    if (normalized.duration > 0) {
      window.setTimeout(() => {
        dismissAlert(id);
      }, normalized.duration);
    }

    return id;
  }, [dismissAlert]);

  const value = React.useMemo(() => ({
    alerts,
    pushAlert,
    dismissAlert,
    clearAlerts: () => setAlerts([]),
  }), [alerts, pushAlert, dismissAlert]);

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>;
}

export function useAlerts() {
  const context = React.useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used inside an AlertProvider.');
  }
  return context;
}

const TYPE_STYLES = {
  success: 'border-emerald-400/30 bg-emerald-500/15 text-emerald-100',
  error: 'border-rose-400/30 bg-rose-500/15 text-rose-100',
  warning: 'border-amber-400/30 bg-amber-500/15 text-amber-100',
  info: 'border-cyan-400/30 bg-cyan-500/15 text-cyan-100',
};

export function AlertStack() {
  const { alerts, dismissAlert } = useAlerts();

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[250] flex w-[min(92vw,24rem)] flex-col gap-3">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`pointer-events-auto rounded-2xl border px-4 py-3 shadow-2xl backdrop-blur-md ${TYPE_STYLES[alert.type] || TYPE_STYLES.info}`}
          role={alert.type === 'error' ? 'alert' : 'status'}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              {alert.title && <p className="text-sm font-semibold">{alert.title}</p>}
              <p className="mt-1 text-sm leading-6">{alert.message}</p>
            </div>
            <button
              type="button"
              onClick={() => dismissAlert(alert.id)}
              className="rounded-full border border-white/10 bg-white/10 px-2 py-1 text-xs font-semibold text-white/80 transition-colors hover:bg-white/20 hover:text-white"
              aria-label="Dismiss alert"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
